<template>
	<div class="lending-side-menu-root">
		<div
			class="lending-side-menu"
			:class="{ open: visible, 'pc-menu': isPc }"
			v-show="visible"
		>
			<div v-if="isPc" class="menu-mask" @click="$emit('close')"></div>
			<div class="menu-panel">
				<div v-if="isLogin && userUid" class="menu-uid-row">
					<span class="uid-label">{{ $t('lendingMenu.uid') }}</span>
					<span class="uid-value">{{ userUid }}</span>
					<button
						type="button"
						class="uid-copy-btn"
						:aria-label="$t('lendingMenu.copyUid')"
						@click.stop="copyUid"
					>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect x="5.5" y="5.5" width="8" height="8" rx="1.2" stroke="currentColor" stroke-width="1.2"/>
							<path d="M4.5 10.5h-1a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v1" stroke="currentColor" stroke-width="1.2"/>
						</svg>
					</button>
				</div>
				<div
					v-if="showGetStarted"
					class="menu-item join-entry"
					:class="{ disabled: joinLoading }"
					@click="openGetStarted"
				>
					<span class="menu-icon" v-html="joinIcon"></span>
					<span class="menu-label">{{ $t('lendingMenu.getStarted') }}</span>
				</div>
				<div
					class="menu-item"
					:class="{ active: isHomeActive }"
					@click="goHome"
				>
					<span class="menu-icon" v-html="homeIcon"></span>
					<span class="menu-label">{{ $t('lendingMenu.home') }}</span>
				</div>
				<div
					v-for="item in configMenuItems"
					:key="item.type"
					class="menu-item"
					:class="{ active: isActive(item.type) }"
					@click="onSelect(item)"
				>
					<span class="menu-icon" v-html="item.icon"></span>
					<span class="menu-label">{{ $t(item.labelKey) }}</span>
				</div>
				<div
					class="menu-item"
					:class="{ active: isAssetsActive }"
					@click="goAssets"
				>
					<span class="menu-icon" v-html="assetsIcon"></span>
					<span class="menu-label">{{ $t('lendingMenu.assets') }}</span>
				</div>
				<div
					v-for="item in configMenuItemsAfter"
					:key="item.type"
					class="menu-item"
					:class="{ active: isActive(item.type) }"
					@click="onSelect(item)"
				>
					<span class="menu-icon" v-html="item.icon"></span>
					<span class="menu-label">{{ $t(item.labelKey) }}</span>
				</div>
				<div class="menu-item" @click="openService">
					<span class="menu-icon" v-html="customerServiceIcon"></span>
					<span class="menu-label">{{ $t('lendingMenu.customerService') }}</span>
				</div>
				<div class="menu-item language-entry" @click="openLanguage">
					<span class="menu-icon" v-html="languageIcon"></span>
					<span class="menu-label">{{ $t('lendingMenu.language') }}</span>
					<span class="lang-current">{{ currentLangName }}</span>
					<svg class="chevron" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M7 4.5L12.5 10L7 15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</div>
				<div class="menu-item language-entry" @click="openTheme">
					<span class="menu-icon" v-html="themeIcon"></span>
					<span class="menu-label">{{ $t('lendingMenu.theme') }}</span>
					<span class="lang-current">{{ currentThemeName }}</span>
					<svg class="chevron" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M7 4.5L12.5 10L7 15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</div>
				<div class="menu-footer">{{ $t('lendingMenu.footerDesc') }}</div>
			</div>

			<Languages :isPc="isPc" :isLanguage="isLanguage" @close="isLanguage = false" />
			<ThemeSheet :visible="isTheme" @close="isTheme = false" />
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Languages from '@/components/Languages.vue'
import ThemeSheet from '@/components/ThemeSheet.vue'
import { WalletUtils } from '@/api/wallet/walletUtils'
import { authorizeLendingAsset, handleAuthorizeError, requireLogin } from '@/utils/lendingGetStarted'
import { openCustomerService } from '@/utils/customerService'

const ICONS = {
	join: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 3.5v13M3.5 10h13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`,
	home: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 9.2L10 3.5l6.5 5.7V16a1 1 0 0 1-1 1h-3.6v-4.2H8.1V17H4.5a1 1 0 0 1-1-1V9.2z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>`,
	assets: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3.5" y="5" width="13" height="10" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M3.5 8.5h13" stroke="currentColor" stroke-width="1.4"/><path d="M7 12h2.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	language: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="7.2" stroke="currentColor" stroke-width="1.4"/><path d="M3 10h14M10 2.8c2 2.2 3 4.5 3 7.2s-1 5-3 7.2M10 2.8C8 5 7 7.3 7 10s1 5 3 7.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	theme: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="3.2" stroke="currentColor" stroke-width="1.4"/><path d="M10 2.5v1.6M10 15.9v1.6M2.5 10h1.6M15.9 10h1.6M4.7 4.7l1.1 1.1M14.2 14.2l1.1 1.1M4.7 15.3l1.1-1.1M14.2 5.8l1.1-1.1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	yieldLadder: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 14.5L7.2 10.2L10.2 12.8L16.5 5.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M12.5 5.5h4v4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
	whitepaper: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 3.5h6.2L15.5 7.3v9.2a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1z" stroke="currentColor" stroke-width="1.4"/><path d="M11.5 3.5V7.5H15.5" stroke="currentColor" stroke-width="1.4"/><path d="M7 10.5h6M7 13.5h6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	rulesSecurity: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2.5L15.5 5v4.2c0 3.5-2.3 6.6-5.5 7.8-3.2-1.2-5.5-4.3-5.5-7.8V5L10 2.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M8 10.2l1.4 1.4L12.4 8.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
	customerService: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 4.5h12a1 1 0 0 1 1 1v6.5a1 1 0 0 1-1 1H8.8L5 15.5V5.5a1 1 0 0 1 1-1z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M7 8.5h6M7 11h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`
}

export default {
	name: 'LendingSideMenu',
	components: { Languages, ThemeSheet },
	props: {
		visible: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			isLanguage: false,
			isTheme: false,
			joinLoading: false,
			walletUtils: null,
			joinIcon: ICONS.join,
			homeIcon: ICONS.home,
			assetsIcon: ICONS.assets,
			languageIcon: ICONS.language,
			themeIcon: ICONS.theme,
			customerServiceIcon: ICONS.customerService,
			configMenuItems: [
				{ type: 'yieldLadder', labelKey: 'lendingMenu.yieldLadder', icon: ICONS.yieldLadder }
			],
			configMenuItemsAfter: [
				{ type: 'whitepaper', labelKey: 'lendingMenu.whitepaper', icon: ICONS.whitepaper },
				{ type: 'rulesSecurity', labelKey: 'lendingMenu.rulesSecurity', icon: ICONS.rulesSecurity }
			]
		}
	},
	computed: {
		...mapState(['isPc', 'currentLanguage', 'themeMode', 'isLogin', 'userInfo']),
		...mapGetters(['langList', 'langIndex', 'hasWalletTokenAuthorized', 'walletAuthStatusLoaded']),
		isHomeActive() {
			return this.$route.name === 'home'
		},
		isAssetsActive() {
			return this.$route.name === 'lendingAssets'
		},
		currentLangName() {
			const item = (this.langList || [])[this.langIndex]
			if (item && item.name) return item.name
			const found = (this.langList || []).find((i) => i.value === this.currentLanguage)
			return found ? found.name : (this.currentLanguage || 'en')
		},
		currentThemeName() {
			const map = {
				auto: 'lendingTheme.auto',
				light: 'lendingTheme.light',
				dark: 'lendingTheme.dark'
			}
			return this.$t(map[this.themeMode] || map.auto)
		},
		userUid() {
			const id = this.userInfo && (this.userInfo.publicUid || this.userInfo.id)
			if (id === null || id === undefined || id === '') return ''
			return String(id)
		},
		showGetStarted() {
			return false
		}
	},
	watch: {
		visible(val) {
			document.body.style.overflow = val ? 'hidden' : ''
			if (!val) {
				this.isLanguage = false
				this.isTheme = false
			}
		}
	},
	beforeDestroy() {
		document.body.style.overflow = ''
	},
	methods: {
		isActive(type) {
			return this.$route.name === 'lendingContent' && this.$route.query.type === type
		},
		onSelect(item) {
			this.$emit('close')
			this.$router.push({
				name: 'lendingContent',
				query: { type: item.type }
			}).catch(() => {})
		},
		goHome() {
			this.$emit('close')
			if (this.$route.name !== 'home') {
				this.$router.push({ name: 'home' }).catch(() => {})
			}
		},
		goAssets() {
			this.$emit('close')
			if (this.$route.name !== 'lendingAssets') {
				this.$router.push({ name: 'lendingAssets' }).catch(() => {})
			}
		},
		openService() {
			this.$emit('close')
			openCustomerService(this)
		},
		openLanguage() {
			this.isLanguage = true
		},
		openTheme() {
			this.isTheme = true
		},
		async copyUid() {
			const text = this.userUid
			if (!text) return
			try {
				if (navigator.clipboard && navigator.clipboard.writeText) {
					await navigator.clipboard.writeText(text)
				} else {
					const textarea = document.createElement('textarea')
					textarea.value = text
					textarea.setAttribute('readonly', '')
					textarea.style.position = 'fixed'
					textarea.style.opacity = '0'
					document.body.appendChild(textarea)
					textarea.select()
					document.execCommand('copy')
					document.body.removeChild(textarea)
				}
				this.$message({
					message: this.$t('toast.copySuccess'),
					type: 'success'
				})
			} catch (e) {
				console.error('Copy UID failed', e)
			}
		},
		async openGetStarted() {
			if (this.joinLoading) return
			this.$emit('close')
			this.joinLoading = true
			try {
				if (!requireLogin(this)) return
				if (!this.walletUtils) {
					this.walletUtils = new WalletUtils()
				}
				await authorizeLendingAsset(this, 'USDT', { walletUtils: this.walletUtils })
			} catch (e) {
				handleAuthorizeError(this, e)
			} finally {
				this.joinLoading = false
			}
		}
	}
}
</script>

<style lang="scss" scoped>
/* 移动端：全屏菜单 */
.lending-side-menu {
	position: fixed;
	inset: 0;
	z-index: 10;
	background: var(--ld-bg, #fff);
	color: var(--ld-text, #111);
	padding-top: calc(0.72rem + env(safe-area-inset-top, 0px));
	padding-bottom: env(safe-area-inset-bottom, 0px);
	box-sizing: border-box;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

.menu-panel {
	padding: 0.12rem 0.16rem 0.4rem;
}

.menu-uid-row {
	display: flex;
	align-items: center;
	gap: 0.08rem;
	padding: 0.1rem 0.16rem 0.14rem;
	margin-bottom: 0.02rem;
	font-size: 0.16rem;
	line-height: 1.2;
	color: var(--ld-text-secondary, #888);
}

.uid-label {
	flex-shrink: 0;
	font-weight: 500;
	color: var(--ld-text-secondary, #888);
}

.uid-value {
	flex-shrink: 0;
	color: var(--ld-text, #111);
	font-variant-numeric: tabular-nums;
}

.uid-copy-btn {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 0.24rem;
	height: 0.24rem;
	padding: 0;
	border: none;
	border-radius: 0.06rem;
	background: transparent;
	color: var(--ld-text-secondary, #888);
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;

	&:hover,
	&:active {
		color: var(--ld-text, #111);
		background: var(--ld-bg-active, #f2f2f2);
	}

	svg {
		display: block;
		width: 0.14rem;
		height: 0.14rem;
	}
}

.lending-side-menu.pc-menu .menu-uid-row {
	padding: 8px 14px 12px;
	margin-bottom: 4px;
	font-size: 15px;
}

.lending-side-menu.pc-menu .uid-copy-btn {
	width: 22px;
	height: 22px;
	border-radius: 6px;

	svg {
		width: 14px;
		height: 14px;
	}
}

/* PC：左侧抽屉 + 遮罩 */
.lending-side-menu.pc-menu {
	background: transparent;
	padding: 0;
	overflow: hidden;
	display: flex;
}

.lending-side-menu.pc-menu .menu-mask {
	position: absolute;
	inset: 0;
	background: var(--ld-overlay, rgba(17, 17, 17, 0.28));
}

.lending-side-menu.pc-menu .menu-panel {
	position: relative;
	z-index: 1;
	width: 300px;
	max-width: 86vw;
	height: 100%;
	margin: 0;
	padding: 80px 16px 24px;
	background: var(--ld-bg-card, #fff);
	border-right: 1px solid var(--ld-border, #ececec);
	box-shadow: var(--ld-menu-shadow, 4px 0 24px rgba(0, 0, 0, 0.06));
	overflow-y: auto;
	animation: lending-menu-slide-in 0.22s ease-out;
	box-sizing: border-box;
}

.lending-side-menu.pc-menu .menu-item {
	padding: 12px 14px;
	font-size: 15px;
	gap: 10px;
	border-radius: 12px;
}

.lending-side-menu.pc-menu .menu-icon {
	width: 22px;
	height: 22px;

	::v-deep svg {
		width: 22px;
		height: 22px;
	}
}

.lending-side-menu.pc-menu .menu-item.join-entry {
	.menu-icon,
	.menu-label {
		height: 22px;
		line-height: 22px;
	}
}

.lending-side-menu.pc-menu .language-entry .lang-current {
	font-size: 13px;
}

@keyframes lending-menu-slide-in {
	from {
		transform: translateX(-100%);
	}
	to {
		transform: translateX(0);
	}
}

.menu-item.join-entry {
	font-weight: 600;
	color: var(--ld-btn-bg, #111);

	.menu-icon,
	.menu-label {
		height: 0.24rem;
		line-height: 0.24rem;
	}

	&.disabled {
		opacity: 0.6;
		pointer-events: none;
	}
}

.menu-item {
	display: flex;
	align-items: center;
	gap: 0.08rem;
	padding: 0.14rem 0.16rem;
	border-radius: 0.14rem;
	font-size: 0.17rem;
	line-height: 1;
	color: var(--ld-text, #111);
	cursor: pointer;
	user-select: none;
	-webkit-tap-highlight-color: transparent;

	&.active,
	&:active {
		background: var(--ld-bg-active, #f2f2f2);
	}
}

.menu-icon {
	width: 0.24rem;
	height: 0.24rem;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: inherit;
	flex-shrink: 0;
	line-height: 0;

	::v-deep svg {
		width: 0.24rem;
		height: 0.24rem;
		display: block;
	}
}

.menu-label {
	flex: 1;
	line-height: 0.24rem;
	display: flex;
	align-items: center;
	min-width: 0;
}

.language-entry .lang-current {
	color: var(--ld-text-secondary, #8a8a8a);
	font-size: 0.14rem;
	margin-right: 0.02rem;
	flex-shrink: 0;
}

.language-entry .chevron {
	color: var(--ld-text-secondary, #8a8a8a);
	flex-shrink: 0;
	display: block;
}

.menu-footer {
	margin-top: 0.24rem;
	padding: 0.16rem 0.16rem 0;
	font-size: 0.14rem;
	line-height: 1.55;
	color: var(--ld-text-secondary, #8a8a8a);
	border-top: 1px solid var(--ld-border, #ececec);
}

.lending-side-menu.pc-menu .menu-footer {
	margin-top: 24px;
	padding: 16px 14px 0;
	font-size: 14px;
	line-height: 1.6;
}
</style>
